import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Lock, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('username', email);
      formData.append('password', password);

      const response = await axios.post(`${API}/auth/login`, formData);
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('msg.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand flex items-center justify-center" dir={dir}>
      {/* Language Selector - Fixed Position */}
      <div className="fixed top-4 left-4 z-50">
        <LanguageSelector />
      </div>
      
      <div className="w-full max-w-md px-6">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Logo size="lg" />
            <h1 className="text-2xl font-heading font-bold text-primary">
              {t('login.title')}
            </h1>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md flex items-center gap-3" data-testid="login-error">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-700 font-body">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium text-primary mb-2 ${isRTL ? 'text-right' : 'text-left'} font-body`}>
                {t('login.email')}
              </label>
              <div className="relative">
                <Mail className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400`} />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body`}
                  placeholder="example@email.com"
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium text-primary mb-2 ${isRTL ? 'text-right' : 'text-left'} font-body`}>
                {t('login.password')}
              </label>
              <div className="relative">
                <Lock className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400`} />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold focus:border-transparent outline-none transition-all font-body`}
                  placeholder="••••••••"
                />
              </div>
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-white py-3 rounded-md hover:bg-primary-900 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 font-heading font-medium"
            >
              {loading ? t('msg.loading') : t('login.loginBtn')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 font-body">
              {t('login.noAccount')}{' '}
              <button
                data-testid="login-to-register-link"
                onClick={() => navigate('/register')}
                className="text-gold hover:text-gold-dark font-semibold"
              >
                {t('login.createAccount')}
              </button>
            </p>
          </div>

          <div className="mt-6 text-center">
            <button
              data-testid="login-back-home-btn"
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-primary font-body"
            >
              {t('nav.home')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
